import type { TerraApiRouteResponses } from '@terra/shared-utils';
import type { TerraApiRouteInput } from '@terra/shared-utils';
import {
  callWithOrchestratorHeaders,
  tenantAwareApiWrapper,
  tenantAwareApiWrapperOrNull,
  withOrchestratorClient,
} from './portal-http';
import { findingsClient } from '@terra/shared-rest-clients-findings';
import type {
  DecideSeverityChangeRequestBody,
  FindingsContract,
  FindingsGroupByField,
  FindingUpdateBodyDto,
  FindingsListSelection,
  LinkedTicketProvider,
} from '@terra/shared-contracts-findings';
import { toBulkFilterBody } from '../types';
import type { FindingsBulkFilterPayload, FindingsBulkUpdate } from '../types';

export function createFindingsApi() {
  const api = {
    getFindingStatsByApplicationId: (applicationId: string) =>
      tenantAwareApiWrapperOrNull<
        typeof FindingsContract.getStatsByApplicationId
      >({
        onFailureMessage: 'Failed to get finding stats by application',
        endpointFn: (headers) =>
          findingsClient.getStatsByApplicationId({
            params: { id: applicationId },
            ...withOrchestratorClient(headers),
          }),
      }),

    getStatusDistributionByApplicationId: (applicationId: string) =>
      tenantAwareApiWrapperOrNull<
        typeof FindingsContract.getStatusDistributionByApplicationId
      >({
        onFailureMessage:
          'Failed to get finding status distribution by application',
        endpointFn: (headers) =>
          findingsClient.getStatusDistributionByApplicationId({
            params: { id: applicationId },
            ...withOrchestratorClient(headers),
          }),
      }),

    getFindingById: ({
      findingId,
      targetId,
    }: {
      findingId: string;
      targetId: string;
    }) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.getFinding({
            params: { id: findingId },
            query: { includeUnpublished: true, targetId },
            ...withOrchestratorClient(headers),
          }),
      }),

    getFindingTimeline: (findingId: string) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.getFindingTimeline({
            params: { id: findingId },
            ...withOrchestratorClient(headers),
          }),
      }),

    getAuditLogs: (findingId: string, skip = 0, limit = 50) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.getAuditLogs({
            params: { id: findingId },
            query: { skip, limit },
            ...withOrchestratorClient(headers),
          }),
      }),

    getAllFindingsEnriched: (
      query: TerraApiRouteInput<
        typeof FindingsContract.listFindingsEnriched
      >['query'],
    ) =>
      tenantAwareApiWrapper<typeof FindingsContract.listFindingsEnriched>({
        endpointFn: (headers) =>
          findingsClient.listFindingsEnriched({
            query,
            ...withOrchestratorClient(headers),
          }),
      }),

    getFindingLinkedAssetCounts: (
      query: TerraApiRouteInput<
        typeof FindingsContract.getFindingLinkedAssetCounts
      >['query'],
    ) =>
      tenantAwareApiWrapper<
        typeof FindingsContract.getFindingLinkedAssetCounts
      >({
        endpointFn: (headers) =>
          findingsClient.getFindingLinkedAssetCounts({
            query,
            ...withOrchestratorClient(headers),
          }),
      }),

    createFinding: (data: Partial<unknown>) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.createFinding({
            body: data as never,
            ...withOrchestratorClient(headers),
          }),
      }),

    updateFinding: (findingId: string, data: FindingUpdateBodyDto) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.updateFinding({
            params: { id: findingId },
            body: data,
            ...withOrchestratorClient(headers),
          }),
      }),

    requestFindingRetest: (findingId: string) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.requestFindingRetest({
            params: { id: findingId },
            body: {},
            ...withOrchestratorClient(headers),
          }),
      }),

    decideSeverityChangeRequest: ({
      findingId,
      requestId,
      body,
    }: {
      findingId: string;
      requestId: string;
      body: DecideSeverityChangeRequestBody;
    }) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.decideSeverityChangeRequest({
            params: { id: findingId, requestId },
            body,
            ...withOrchestratorClient(headers),
          }),
      }),

    abortFindingRetest: ({
      findingId,
      threadId,
      executionId,
    }: {
      findingId: string;
      threadId?: string;
      executionId?: string;
    }) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.abortFindingRetest({
            params: { id: findingId },
            body: {
              ...(threadId ? { threadId } : {}),
              ...(executionId ? { executionId } : {}),
            },
            ...withOrchestratorClient(headers),
          }),
      }),

    replaceFindingLinkedTicket: ({
      findingId,
      provider,
      ticketUrl,
    }: {
      findingId: string;
      provider: LinkedTicketProvider;
      ticketUrl: string;
    }) =>
      tenantAwareApiWrapper<typeof FindingsContract.replaceLinkedTicket>({
        endpointFn: (headers) =>
          findingsClient.replaceLinkedTicket({
            params: { id: findingId },
            body: {
              provider,
              ticketUrl,
            },
            ...withOrchestratorClient(headers),
          }) as Promise<
            TerraApiRouteResponses<typeof FindingsContract.replaceLinkedTicket>
          >,
      }),

    createExternalNetworkFindingChain: ({
      externalNetworkId,
      findingIds,
    }: {
      externalNetworkId: string;
      findingIds: string[];
    }) =>
      tenantAwareApiWrapper<
        typeof FindingsContract.createExternalNetworkFindingChain
      >({
        useBodyMessage: true,
        endpointFn: (headers) =>
          findingsClient.createExternalNetworkFindingChain({
            body: { externalNetworkId, findingIds },
            ...withOrchestratorClient(headers),
          }),
      }),

    deleteFinding: (findingId: string) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.deleteFinding({
            params: { id: findingId },
            ...withOrchestratorClient(headers),
          }),
      }),

    deleteFindingsBulk: (findingIds: string[]) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.deleteFindingsBulk({
            body: { ids: findingIds },
            ...withOrchestratorClient(headers),
          }),
      }),

    updateFindingsBulk: (findingIds: string[], updates: FindingsBulkUpdate) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.updateFindingsBulk({
            body: {
              ids: findingIds,
              update: updates,
            },
            ...withOrchestratorClient(headers),
          }),
      }),

    countFindingsByFilter: (
      payload: FindingsBulkFilterPayload,
    ): Promise<number> =>
      tenantAwareApiWrapper<typeof FindingsContract.countFindingsByFilter>({
        endpointFn: (headers) =>
          findingsClient.countFindingsByFilter({
            body: toBulkFilterBody({ payload }),
            ...withOrchestratorClient(headers),
          }),
      }).then((body) => body.count),

    countFindingsGroupsByFilter: ({
      payload,
      groupBy,
      timeZone,
    }: {
      payload: Omit<FindingsBulkFilterPayload, 'excludedIds'>;
      groupBy: FindingsGroupByField;
      timeZone?: string;
    }) => {
      const { excludedIds: _excludedIds, ...filterBody } = toBulkFilterBody({
        payload: { ...payload, excludedIds: [] },
      });

      return tenantAwareApiWrapper<
        typeof FindingsContract.countFindingsGroupsByFilter
      >({
        endpointFn: (headers) =>
          findingsClient.countFindingsGroupsByFilter({
            body: { ...filterBody, groupBy, timeZone },
            ...withOrchestratorClient(headers),
          }),
      }).then((body) => body.groups);
    },

    resolveFindingsListSelection: (body: FindingsListSelection) =>
      tenantAwareApiWrapper<
        typeof FindingsContract.resolveFindingsListSelection
      >({
        endpointFn: (headers) =>
          findingsClient.resolveFindingsListSelection({
            body,
            ...withOrchestratorClient(headers),
          }),
      }),

    deleteFindingsBulkByFilter: (payload: FindingsBulkFilterPayload) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.deleteFindingsBulkByFilter({
            body: toBulkFilterBody({ payload }),
            ...withOrchestratorClient(headers),
          }),
      }),

    updateFindingsBulkByFilter: (
      payload: FindingsBulkFilterPayload,
      updates: FindingsBulkUpdate,
    ) =>
      callWithOrchestratorHeaders({
        call: (headers) =>
          findingsClient.updateFindingsBulkByFilter({
            body: {
              ...toBulkFilterBody({ payload }),
              update: updates,
            },
            ...withOrchestratorClient(headers),
          }),
      }),
  };

  return api;
}
